/**
 * CetusEd Admin Console — District List
 *
 * COMPLIANCE: FERPA §1232g(b)(4)(B) — Vendor-hosted platform must track all
 *   districts with their deployment status. This view is restricted to admin
 *   users with 'admin:district:list' capability.
 *
 * MIGRATION TARGET: New view for the admin console. The monolith has no
 *   cross-district management capability.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useDistricts } from '../../api/hooks';
import { DataTable, type Column } from '../../ui/DataTable';
import type { DistrictSummary } from '../../api/hooks';

export const DistrictList: React.FC = () => {
  const { data: districts, isLoading, error } = useDistricts();

  const columns: Column<DistrictSummary>[] = [
    { key: 'name', header: 'District', sortable: true },
    { key: 'state', header: 'State', sortable: true, width: '80px' },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      width: '120px',
      render: (row) => (
        <span className={`status-badge status-badge--${row.status}`}>
          {row.status}
        </span>
      ),
    },
    { key: 'deploymentMode', header: 'Mode', sortable: true, width: '120px' },
    { key: 'userCount', header: 'Users', sortable: true, width: '80px' },
    { key: 'studentCount', header: 'Students', sortable: true, width: '80px' },
    {
      key: 'storageUsedBytes',
      header: 'Storage',
      sortable: true,
      width: '100px',
      render: (row) => {
        const mb = (row.storageUsedBytes / 1024 / 1024).toFixed(1);
        return <span>{mb} MB</span>;
      },
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
  ];

  if (error) {
    return (
      <div className="admin-error" role="alert">
        Unable to load districts. Please try again later.
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1>Districts</h1>
        <Link to="/districts/provision" className="admin-btn admin-btn--primary">
          Provision New
        </Link>
      </div>

      <DataTable
        columns={columns}
        data={districts || []}
        keyExtractor={(d) => d.id}
        isLoading={isLoading}
        emptyMessage="No districts found. Provision a new district to get started."
      />
    </div>
  );
};
